import { useField } from 'formik';
import React from 'react';

import {
  DateTimePicker as MUIDateTimePicker,
  DateTimePickerProps as MUIDateTimePickerProps,
} from '@mui/x-date-pickers';
import { CalendarDateService, DateObject } from '@uplift/services';
import { TextFieldProps } from '@mui/material';
import { BaseTextField } from '../BaseTextField';


const DatePickerOpenedContext = React.createContext(false);


export type DateTimePickerProps = Omit<MUIDateTimePickerProps, 'onChange' | 'value' | 'renderInput' | 'inputFormat'> & {
  name: string;
  color?: 'primary' | 'secondary';
  label?: React.ReactNode;
  fullWidth?: boolean;
  valueFormat?: string;
  hideError?: boolean;
  helperText?: string;
  format?: string;
  placeholder?: string;
  TextFieldComponent?: React.ComponentType<TextFieldProps>;
  style?: TextFieldProps['style'];
  transformValue?: (value: DateObject) => DateObject;
};


export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  name,
  helperText,
  format,
  fullWidth,
  TextFieldComponent = BaseTextField,
  placeholder,
  style,
  transformValue,
  ...props
}) => {
  const [ open, setOpen ] = React.useState(false);
  const [ field, meta, helpers ] = useField(name);

  const onChange = (date: MUIDateTimePickerProps['value']) => {
    if (CalendarDateService.isDateObject(date) && props.valueFormat) {
      helpers.setValue(date.format(props.valueFormat));
    } else {
      if (date && transformValue) {
        helpers.setValue(transformValue(date));
      } else {
        helpers.setValue(date);
      }
    }
  };

  const valueDateObj = React.useMemo(() => {
    if (!field.value) {
      return null;
    }

    if (CalendarDateService.isDateObject(field.value)) {
      return field.value;
    }

    if (typeof field.value === 'string' && props.valueFormat) {
      return CalendarDateService.date(field.value, props.valueFormat);
    }

    return field.value;
  }, [ field.value, props.valueFormat ]);

  const textFieldProps: TextFieldProps = {
    fullWidth: fullWidth,
    color: props.color,
    placeholder: placeholder,
    style: style,
    error: meta.touched && Boolean(meta.error),
    helperText: (meta.touched && meta.error) || helperText,
    onBlur: () => helpers.setTouched(true),
  };

  return (
    <DatePickerOpenedContext.Provider value={open}>
      <MUIDateTimePicker
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        enableAccessibleFieldDOMStructure={false}
        closeOnSelect
        {...props}
        slots={{
          ...props.slots,
          textField: TextFieldComponent,
          toolbar: () => null,
          actionBar: () => null,
        }}
        slotProps={{
          ...props.slotProps,
          textField: textFieldProps,
          popper: {
            onBlur: () => helpers.setTouched(true),
          }
        }}
        format={format}
        value={valueDateObj}
        onChange={onChange}
      />
    </DatePickerOpenedContext.Provider>
  );
};
